import React, { createContext, useState, PropsWithChildren, useEffect } from "react";
import { theme, themes } from "@/constants/themes";
import { useStorage } from "@/hooks/useStorage";
import { Appearance, Platform } from "react-native";
import { ThemeProvider as OobThemeProvider, DarkTheme, DefaultTheme } from "@react-navigation/native";
import type { ThemeContextProps } from "@/types/providers/theme";

export const ThemeContext = createContext<ThemeContextProps>({
  theme: themes.light,
  toggleTheme: () => {},
});

const THEME_STORAGE_KEY = "@app_theme";

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setTheme] = useState<theme>(Appearance.getColorScheme() === "dark" ? themes.dark : themes.light);
  const storage = useStorage();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const loadSavedTheme = async () => {
      // Early exit for native applications
      if (Platform.OS !== "web") {
        setTheme(Appearance.getColorScheme() === "dark" ? themes.dark : themes.light);
        setIsReady(true);
        return;
      }
      try {
        const savedTheme = storage.getItem(THEME_STORAGE_KEY);
        if (savedTheme === "dark") {
          setTheme(themes.dark);
        } else {
          setTheme(themes.light);
        }
      } catch (error: any) {
        console.error("Failed to load theme:", error.message);
      } finally {
        setIsReady(true);
      }
    };

    loadSavedTheme();
  }, [storage]);

  const toggleTheme = async () => {
    const newTheme = theme === themes.light ? themes.dark : themes.light;
    setTheme(newTheme);
    try {
      storage.saveFast(THEME_STORAGE_KEY, newTheme === themes.light ? "light" : "dark");
    } catch (error: any) {
      throw Error(error.message);
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    if (theme.name === "dark") {
      root.style.setProperty("--scrollbar-thumb", "rgba(255,255,255,0.45)");
      root.style.setProperty("--scrollbar-thumb-hover", "rgba(255,255,255,0.8)");
      root.style.setProperty("--scrollbar-track", "rgba(0,0,0,0.15)");
      root.style.setProperty("color-scheme", "dark");
    } else {
      root.style.setProperty("--scrollbar-thumb", "rgba(0,0,0,0.35)");
      root.style.setProperty("--scrollbar-thumb-hover", "rgba(0,0,0,0.6)");
      root.style.setProperty("--scrollbar-track", "rgba(0,0,0,0.06)");
      root.style.setProperty("color-scheme", "light");
    }
  }, [theme]);

  if (isReady) {
    return (
      <OobThemeProvider
        value={{
          dark: theme.name === "dark",
          fonts: theme.name === "dark" ? DarkTheme.fonts : DefaultTheme.fonts,
          colors: {
            background: theme.background,
            border: theme.border,
            card: theme.card,
            primary: theme.primary,
            text: theme.text,
            notification: theme.name === "dark" ? DarkTheme.colors.notification : DefaultTheme.colors.notification,
          },
        }}
      >
        <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
      </OobThemeProvider>
    );
  }
}
